<template>
  <BaseModal
    hookSufix="BandwidthDetailsModal"
    :title="$t('message.pages.analytics.bandwidthDetailsModal.title')"
    fullscreen
    min-height="100dvh"
    overflow="scroll"
    persistent
    :isModalBandwidth="true"
    :close-on-click-modal="false"
  >
    <template #subtitle>
      <span class="body-regular-smaller"
        >{{ $t('message.pages.analytics.bandwidthDetailsModal.subtitle') }}
        <a type="secondary" @click="openHelpArticle" class="body-bold-smaller"
          >{{ $t('message.pages.analytics.bandwidthDetailsModal.knowMore') }}
          <v-icon size="10">mdi-open-in-new</v-icon></a
        ></span
      >
    </template>

    <template #content>
      <div class="modal-content-wrapper bandwidth-modal">
        <div class="fixed-header pb-0">
          <v-row class="BandwidthDetailsModal">
            <v-col cols="7" class="d-flex ga-2">
              <date-picker
                :clearable="false"
                :disabled-date="verifyDisabledDate"
                popup-class="analyticsDatePicker"
                style="width: 260px; margin-top: 1px"
                v-model="selectedDate"
                editable
                type="date"
                :format="'dd/MM/yyyy'"
                :placeholder="
                  $t('message.pages.analytics.bandwidthDetailsModal.selectDate')
                "
                append-to-body
                @change="handleDateChange"
              >
                <template v-slot:input>
                  <v-text-field
                    :class="{ analyticsPickerField: $vuetify.theme.dark }"
                    dense
                    :value="selectedDateValue"
                    outlined
                    prepend-inner-icon="mdi-calendar-month"
                    readonly
                    hide-details
                    class="pickerInput"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon class="mr-2"> mdi-calendar-month </v-icon>
                    </template>
                  </v-text-field>
                </template>
              </date-picker>
              <div class="ml-4" style="width: 100%">
                <search-videos-menu isBandwidthModal />
              </div>
            </v-col>
          </v-row>

          <div class="table-header" ref="tableHeader">
            <div class="tableHeaderTitles d-flex justify-space-between mx-4">
              <div
                class="titleHeader d-flex"
                v-for="(header, i) in tableHeaders"
                :key="i"
                :style="`width: ${header.width}%`"
                :class="{
                  ' justify-end pr-4':
                    tableHeaders[tableHeaders.length - 1].value ===
                    header.value,
                }"
              >
                <span class="body-label-xs d-flex ml-4 align-center">
                  <template v-if="header.value === 'st'">
                    {{
                      $t('message.pages.analytics.bandwidthDetailsModal.date')
                    }}
                    (UTC+0)
                  </template>
                  <template v-else-if="header.value === 'i'">
                    {{
                      $t('message.pages.analytics.bandwidthDetailsModal.userIp')
                    }}
                  </template>
                  <template v-else-if="header.value === 'videoId'">
                    {{
                      $t('message.pages.analytics.bandwidthDetailsModal.video')
                    }}
                  </template>
                  <template v-else-if="header.value === 'type'">
                    {{
                      $t('message.pages.analytics.bandwidthDetailsModal.type')
                    }}
                  </template>
                  <template v-else-if="header.value === 'd'">
                    {{
                      $t('message.pages.analytics.bandwidthDetailsModal.device')
                    }}
                  </template>
                  <template v-else-if="header.value === 'b'">
                    {{
                      $t(
                        'message.pages.analytics.bandwidthDetailsModal.consumption'
                      )
                    }}
                  </template>
                  <v-tooltip bottom persistant>
                    <template v-slot:activator="{ on, attrs }">
                      <img
                        v-if="header.icon"
                        v-bind="attrs"
                        v-on="{ ...on }"
                        class="ml-2"
                        src="@/assets/info-icon.svg"
                        alt="Info Icon"
                      />
                    </template>
                    <span
                      v-if="header.value === 'type'"
                      class="d-flex flex-column"
                      style="width: 200px"
                    >
                      <span class="d-flex mb-4">
                        {{
                          $t(
                            'message.pages.analytics.bandwidthDetailsModal.explanationPlay'
                          )
                        }}
                      </span>
                      <span class="d-flex mb-4">
                        {{
                          $t(
                            'message.pages.analytics.bandwidthDetailsModal.explanationDownload'
                          )
                        }}
                      </span>
                      <span class="d-flex">
                        {{
                          $t(
                            'message.pages.analytics.bandwidthDetailsModal.explanationDRM'
                          )
                        }}
                      </span>
                    </span>
                    <span
                      v-else-if="header.value === 'st'"
                      style="width: 200px"
                    >
                      <span class="d-flex">
                        {{
                          $t(
                            'message.pages.analytics.bandwidthDetailsModal.dateExplanation'
                          )
                        }}
                      </span>
                    </span>
                    <span v-else style="width: 200px">
                      <span class="d-flex">
                        {{
                          $t(
                            'message.pages.analytics.bandwidthDetailsModal.consumptionExplanation'
                          )
                        }}
                      </span>
                    </span>
                  </v-tooltip>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="scrollable-content" @scroll="handleScroll">
          <div
            v-if="nothingFound && !fetchingInfos"
            class="emptyStateTrafficModal d-flex justify-center align-center flex-column"
          >
            <span
              class="emptyState--title text-neutral-20- body-regular-large"
              >{{
                $t('message.pages.analytics.bandwidthDetailsModal.empty')
              }}</span
            >
            <span class="emptyState--subtitle body-regular-small">{{
              $t('message.pages.analytics.bandwidthDetailsModal.tryWhenEmpty')
            }}</span>
          </div>
          <v-data-table
            v-else
            :headers="tableHeaders"
            :items="processedBandwidthData"
            :items-per-page="25"
            :sort-by="['st']"
            :sort-desc="[true]"
            :page.sync="page"
            @page-count="pageCount = $event"
            item-class="item-class"
            disable-items-per-page
            class="tableTraffic"
          >
            <template v-slot:body="{ items }">
              <tbody>
                <template v-if="fetchingInfos">
                  <tr v-for="n in 25" :key="`skeleton-${n}`">
                    <td
                      v-for="(header, i) in tableHeaders"
                      :key="i"
                      :style="`width: ${header.width}%`"
                      :class="{
                        'text-right':
                          tableHeaders[tableHeaders.length - 1].value ===
                          header.value,
                      }"
                      class="body-regular-smaller tableHeaderTitles--item"
                    >
                      <v-skeleton-loader
                        type="text"
                        width="100%"
                        height="20"
                        class="mx-auto"
                      />
                    </td>
                  </tr>
                </template>

                <template v-else>
                  <tr v-for="(item, index) in items" :key="index">
                    <td
                      v-for="(header, i) in tableHeaders"
                      :key="i"
                      :style="`width: ${header.width}%`"
                      :class="{
                        'text-right':
                          tableHeaders[tableHeaders.length - 1].value ===
                          header.value,
                      }"
                      class="body-regular-smaller tableHeaderTitles--item"
                    >
                      <span v-if="header.value === 'type'">
                        <v-tag type="neutral">{{ item[header.value] }}</v-tag>
                      </span>
                      <span v-else-if="header.value === 'b'">
                        {{ formatBytes(item[header.value]) }}
                      </span>
                      <span v-else-if="header.value === 'st'">
                        {{ formatTimestamp(item[header.value]) }}
                      </span>
                      <span v-else-if="header.value === 'videoId'">
                        {{ getVideoName(item[header.value]) }}
                      </span>
                      <span
                        class="text-capitalize"
                        v-else-if="header.value === 'd'"
                      >
                        {{
                          item[header.value] === 'Other'
                            ? 'Desktop'
                            : item[header.value]
                        }}
                      </span>
                      <span v-else>
                        {{ item[header.value] }}
                      </span>
                    </td>
                  </tr>
                </template>
              </tbody>
            </template>
          </v-data-table>
          <div
            class="content-footer d-flex justify-space-between align-center my-4"
            v-if="!nothingFound && !fetchingInfos"
          >
            <div class="body-regular-small recordsNumber">
              {{ formatNumberTotalTraffic(processedBandwidthData.length) }}
              {{
                $t(
                  'message.pages.analytics.bandwidthDetailsModal.recordsOfConsume'
                )
              }}
              ({{ page }} de {{ pageCount }})
            </div>
            <v-pagination
              v-model="page"
              :length="pageCount"
              :total-visible="5"
              elevation="0"
              class="paginationTable"
              prev-icon="mdi-arrow-left"
              next-icon="mdi-arrow-right"
            ></v-pagination>
            <div class="body-bold-small mr-14">
              {{ formatBytes(totalBandwidth) }}
              {{
                $t(
                  'message.pages.analytics.bandwidthDetailsModal.bandwidthInADay'
                )
              }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import BaseModal from '@/components/BaseModal.vue'
import SearchVideosMenu from '../../AnalyticsV2/SearchVideosMenu.vue'
import DatePicker from 'vue2-datepicker'
import { format } from 'date-fns/esm'
import exempleJsonData from './result.json'
import bandwidthDetailsApi from '../../../services/bandwidthDetailsApi'

export default {
  name: 'BandwidthModalDetails',
  components: {
    BaseModal,
    DatePicker,
    SearchVideosMenu,
  },
  data: () => ({
    filterDate: null,
    selectedDate: null,
    selectedDateValue: '',
    allVideos: null,
    bandwidthConsumedData: exempleJsonData,
    processedBandwidthData: [],
    recordsNumber: 0,
    page: 1,
    pageCount: 0,
    nothingFound: false,
    toBeFilteredVideos: null,
    hasAnyVideoInFilter: true,
    tableHeaders: [
      {
        align: 'start',
        value: 'st',
        width: 12,
        icon: true,
      },
      {
        align: 'start',
        value: 'i',
        width: 12,
        icon: false,
      },
      {
        align: 'start',
        value: 'videoId',
        width: 38,
        icon: false,
      },
      {
        align: 'start',
        value: 'type',
        width: 12,
        icon: true,
      },

      {
        align: 'end',
        value: 'd',
        width: 12,
        icon: false,
      },
      {
        align: 'end',
        value: 'b',
        width: 12,
        icon: true,
      },
    ],
    fetchingInfos: false,
    totalBandwidth: 0,
    lastFetchedTimestamp: '',
    isScrolled: false,
  }),
  computed: {
    helpArticleUrl() {
      const lang = this.$i18next.language
      if (lang === 'pt' || lang === 'pt-BR') {
        return 'https://help.pandavideo.com/pt-br/article/consumo-detalhado-da-banda-153l0hk/'
      }
      if (lang === 'es') {
        return 'https://help.pandavideo.com/es/article/detalle-del-consumo-de-banda-1ue8k4z/'
      }
      return 'https://help.pandavideo.com/en-us/article/bandwidth-consumption-detail-kvbiml/'
    },
  },
  mounted() {
    const apiDate = this.getDateToFilter()
    this.lastFetchedTimestamp = apiDate

    if (!window.db) {
      this.$root.$emit('initDexie')
    }

    this.getVideos()

    this.$root.$on('openBandwidthDetailsModal', async () => {
      try {
        await this.fetchTrafficDetails(apiDate)
        this.lastFetchedTimestamp = apiDate
      } catch (error) {
        console.log(error)
      }
    })

    this.$root.$on(
      'filterSelectedBandwidth',
      async (selectedVideos, hasAnyVideoInFilter) => {
        const apiDate = this.getDateToFilter()
        this.toBeFilteredVideos = [...selectedVideos]
        this.toBeFilteredVideos = this.toBeFilteredVideos.map(
          (video) => video.video_external_id
        )
        try {
          await this.fetchTrafficDetails(apiDate, this.toBeFilteredVideos)
          this.lastFetchedTimestamp = apiDate
        } catch (error) {
          console.log(error)
        }
        this.hasAnyVideoInFilter = hasAnyVideoInFilter
      }
    )

    this.$root.$on('searchAllBandwidthModal', async () => {
      const apiDate = this.getDateToFilter()
      this.toBeFilteredVideos = null
      await this.fetchTrafficDetails(apiDate, (this.toBeFilteredVideos = null))
    })

    this.$nextTick(() => {
      const scrollableContent = document.querySelector('.scrollable-content')
      if (scrollableContent) {
        scrollableContent.addEventListener('scroll', this.handleScroll)
      }
    })
  },
  beforeDestroy() {
    const scrollableContent = document.querySelector('.scrollable-content')
    if (scrollableContent) {
      scrollableContent.removeEventListener('scroll', this.handleScroll)
    }
  },
  methods: {
    getDateToFilter() {
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      if (!this.selectedDate) {
        this.selectedDate = yesterday
        this.selectedDateValue = format(yesterday, 'dd/MM/yyyy')
        this.lastFetchedTimestamp = this.selectedDate
      }
      const apiDate = format(this.selectedDate, 'yyyy-MM-dd')
      return apiDate
    },
    verifyDisabledDate(date) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const minDate = new Date('2025-05-01')
      minDate.setHours(0, 0, 0, 0)

      const selectedDate = this.selectedDate
        ? new Date(this.selectedDate)
        : null
      if (selectedDate) {
        selectedDate.setHours(0, 0, 0, 0)
      }

      const incomingDate = new Date(date)
      incomingDate.setHours(0, 0, 0, 0)

      return incomingDate >= today || incomingDate < minDate
    },
    handleDateChange() {
      if (!this.selectedDate) return

      const apiDate = format(this.selectedDate, 'yyyy-MM-dd')

      if (apiDate === this.lastFetchedTimestamp) {
        return
      }
      this.lastFetchedTimestamp = apiDate
      this.selectedDateValue = format(this.selectedDate, 'dd/MM/yyyy')
      this.fetchTrafficDetails(apiDate, this.toBeFilteredVideos)
    },
    getVideoName(video_external_id) {
      const video = this.allVideos.find(
        (item) => item.video_external_id === video_external_id
      )
      return video ? video.title : video_external_id
    },
    async getVideos() {
      this.allVideos = await window.db.videos.toArray()
    },
    async fetchTrafficDetails(date, videos = null) {
      try {
        this.fetchingInfos = true
        this.nothingFound = false
        this.processedBandwidthData = []
        this.totalBandwidth = 0

        const { data } = await bandwidthDetailsApi.getTrafficDetails(
          date,
          videos
        )
        if (!data || Object.keys(data).length === 0) {
          this.nothingFound = true
          return
        }

        this.processedBandwidthData = await this.processData(data)
        this.nothingFound = false
      } catch (error) {
        console.error('Erro ao buscar dados:', error)
        this.nothingFound = true
        this.processedBandwidthData = []
        this.totalBandwidth = 0
      } finally {
        this.fetchingInfos = false
      }
    },
    processData(newData) {
      let totalBandwidth = 0
      const result = Object.entries(newData).flatMap(([videoId, items]) =>
        items.map((item) => {
          totalBandwidth += item.b || 0
          return { ...item, videoId, type: 'Play', length: 12 }
        })
      )
      this.totalBandwidth = totalBandwidth
      return result
    },
    openHelpArticle() {
      window.open(this.helpArticleUrl, '_blank')
    },
    formatTimestamp(timestamp) {
      try {
        const date = new Date(Number(timestamp))

        if (isNaN(date.getTime())) {
          throw new Error('Timestamp inválido')
        }

        return format(date, 'HH:mm')
      } catch (error) {
        console.error('Erro:', error)
        return ''
      }
    },
    formatBytes(bytes) {
      if (bytes === 0) return '0 B'

      const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
      let index = 0
      let convertedValue = bytes

      while (convertedValue >= 1024 && index < units.length - 1) {
        convertedValue /= 1024
        index++
      }

      const formattedValue = convertedValue.toFixed(2).replace(/\.0+$/, '')

      return `${formattedValue} ${units[index]}`
    },
    formatNumberTotalTraffic(number) {
      return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    handleScroll(event) {
      const scrollTop = event.target.scrollTop
      this.isScrolled = scrollTop > 5
    },
  },
  watch: {
    selectedDate: {
      handler(newVal) {
        if (newVal) {
          this.selectedDateValue = format(newVal, 'dd/MM/yyyy')
        } else {
          this.selectedDateValue = ''
        }
      },
    },
    isScrolled: {
      handler(newVal) {
        if (this.$refs.tableHeader) {
          if (newVal) {
            this.$refs.tableHeader.classList.add('scrolled')
          } else {
            this.$refs.tableHeader.classList.remove('scrolled')
          }
        }
      },
      immediate: true,
    },
  },
}
</script>

<style lang="scss">
.paginationTable {
  .v-pagination__navigation {
    box-shadow: none;
  }
  .v-pagination__navigation {
    border: 1px solid varFigma(neutral-95-lightest);
    border-radius: 12px;

    &:hover {
      border: 1px solid varFigma(brand-80-light);
    }
  }
  .v-pagination__item {
    border-radius: 12px;
    box-shadow: none;
    border: 1px solid varFigma(neutral-95-lightest);
    &:hover {
      border: 1px solid varFigma(brand-80-light);
    }
  }
  .v-pagination__item.primary {
    box-shadow: none;
    border: 1px solid varFigma(brand-60-base);
    background: varFigma(brand-95-lighter) !important;
    color: varFigma(brand-40-dark) !important;
    &:hover {
      border: 1px solid varFigma(brand-80-light) !important;
      background: varFigma(brand-80-light) !important;
      color: varFigma(brand-30-darker) !important;
    }
  }
}
.BandwidthDetailsModal {
  .mx-datepicker-popup {
    top: 130px !important;
  }
  .v-text-field.v-text-field--enclosed:not(.v-text-field--rounded)
    > .v-input__control
    > .v-input__slot {
    border: 1px solid varFigma(neutral-95-lightest) !important;
    padding: 0 0 0 10px !important;
  }
  .v-menu__content {
    top: 130px !important;
  }
  .v-input__slot fieldset {
    color: varFigma(neutral-95-lightest) !important;
  }

  .v-input input,
  .pickerInput .v-input__prepend-inner .mdi {
    color: varFigma(neutral-40-light) !important;
  }
  .mx-input-wrapper {
    i.mx-icon-calendar {
      display: none;
    }
  }
}
.v-application--is-ltr .v-data-footer__icons-after {
  margin-right: auto !important;
}
.v-data-table {
  .v-data-table__wrapper
    > table
    > tbody
    > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
    background-color: varFigma(neutral-100-white) !important;
  }
  .v-data-table__wrapper
    > table
    > tbody
    > tr:not(:last-child)
    > td:not(.v-data-table__mobile-row) {
    border-bottom: thin solid varFigma(neutral-95-lightest) !important;
  }
  .v-data-footer__pagination {
    margin-left: auto;
    padding: 1rem 0;
  }
  .v-data-footer__select {
    display: none;
  }
  .v-data-table-header {
    display: none;
  }
  .v-data-footer {
    display: none;
  }
}
.v-toolbar__title {
  display: flex;
  flex-direction: column;
}
</style>
<style lang="scss" scoped>
.bandwidth-modal {
  :deep(.v-dialog) {
    border: none !important;
    overflow: hidden !important;
  }

  :deep(.v-dialog .v-sheet.v-card) {
    overflow: auto !important;
  }

  .modal-content-wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: calc(100vh - 180px);
  }

  .fixed-header {
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: varFigma(neutral-100-white) !important;
    padding: 16px 0;
  }

  .scrollable-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0 16px;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .table-header {
    position: sticky;
    top: 120px;
    z-index: 1;
    background: varFigma(neutral-100-white);
    padding: 16px 0;
    border-bottom: 1px solid varFigma(neutral-95-lightest);
    transition: all 0.3s ease;

    &.scrolled {
      box-shadow: 0 8px 16px -4px rgba(0, 0, 0, 0.15);
    }
  }

  .tableHeaderTitles {
    min-width: 1000px;
    background: varFigma(neutral-100-white);

    &--item {
      color: varFigma(neutral-40-light);
    }
  }

  .tableTraffic {
    min-width: 1000px;
    margin-top: 8px;
    flex: 1;
    display: flex;
    flex-direction: column;

    .v-data-table__wrapper {
      flex: 1;
      overflow-y: auto;
    }
  }

  .content-footer {
    min-width: 1000px;
    margin-top: auto;
    padding: 16px 0;
    background: varFigma(neutral-100-white);
  }

  .recordsNumber {
    color: varFigma(neutral-40-light);
  }

  .emptyStateTrafficModal {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;

    .emptyState {
      &--title {
        color: varFigma(neutral-20-dark);
      }
      &--subtitle {
        color: varFigma(neutral-40-light);
      }
    }

    @media (max-height: 1024px) {
      padding: 32dvh 0;
    }
    @media (max-height: 768px) {
      padding: 30dvh 0;
    }
    .body-label-xs {
      color: varFigma(neutral-20-dark);
      font-family: Rubik;
      font-size: 12px;
      font-style: normal;
      font-weight: 500;
      line-height: 100%;
    }
  }
}
</style>
